import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { RestApiService } from '../api/rest-api.service';

import {
  HR_GET_CURRENT_EMPLOYMENT_ENDPOINT,
  HR_GET_MY_ACTIVE_EMPLOYMENTS_COUNT_ENDPOINT,
  HR_GET_MY_EMPLOYMENTS_ENDPOINT,
  HR_GET_EMPLOYMENTS_ENDPOINT,
  HR_GET_EMPLOYMENTS_SEARCH_SCHEMA_ENDPOINT,
  HR_GET_EMPLOYMENT_ENDPOINT,
  HR_GET_EMPLOYMENT_EDIT_CONTEXT_ENDPOINT,
  HR_GET_EMPLOYMENT_HISTORY_ENDPOINT,
  HR_GET_EMPLOYMENT_INVITATION_CONTEXT_ENDPOINT,
  HR_GET_EMPLOYMENT_TIMELINE_ENDPOINT,
  HR_POST_EMPLOYMENT_ENDPOINT,
  HR_POST_EMPLOYMENT_INVITATION_RESPONSE_ENDPOINT,
  HR_GET_EMPLOYMENT_INVITATIONS_ENDPOINT,
  HR_GET_MY_EMPLOYMENT_INVITATIONS_ENDPOINT,
  HR_GET_MY_EMPLOYMENT_INVITATION_CONTEXT_ENDPOINT,
  HR_POST_MY_EMPLOYMENT_INVITATION_RESPONSE_ENDPOINT,
  HR_POST_EMPLOYMENT_INVITATION_RESEND_ENDPOINT,
  HR_POST_EMPLOYMENT_PROMOTION_ENDPOINT,
  HR_POST_EMPLOYMENT_VERSION_ENDPOINT,
  HR_PATCH_EMPLOYMENT_ADMIN_STATUS_ENDPOINT,
  HR_PUT_EMPLOYMENT_ENDPOINT,
} from '../api/api-endpoints';
import { formatString } from '../utils/string.utils';
import { buildSearchQueryString } from '../utils/search-query-params.utils';
import { EmploymentInvitationResponsePayload } from './employment-invitation-flow.service';
import { SearchListRequestPayload } from '../../shared/search-filter-pagination/search-bar/search-bar.types';

export interface EmploymentInvitationContext {
  employment_id: string;
  organisation_id: string;
  organisation_name: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  status: string;
  recipient_had_account: boolean;
  expires_at: string;
  sent_at: string | null;
  send_count: number;
}

export interface EmploymentInvitationResponse {
  employment_id: string;
  organisation_id: string;
  status: string;
}

export interface MyEmployment {
  id: string;
  organisation_id: string;
  organisation_name: string | null;
  employment_position_name: string | null;
  organisation_branch_id: string | null;
  organisation_branch_name: string | null;
  employment_type: string;
  started_at: string;
  ended_at: string | null;
  is_admin: boolean;
  is_owner: boolean;
}

export interface MyEmploymentInvitation {
  id: string;
  employment_id: string;
  organisation_id: string;
  organisation_name: string;
  recipient_email: string;
  expires_at: string;
  employment_position_name: string | null;
  organisation_branch_id: string | null;
  organisation_branch_name: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class EmploymentServices {
  private employmentSubject = new BehaviorSubject<any | null>({});
  employmentData$ = this.employmentSubject.asObservable();
  employment: any;

  constructor(
    private restApi: RestApiService,
  ) {}

  async getSearchFilterSchema() {
    return await firstValueFrom(this.restApi.get(HR_GET_EMPLOYMENTS_SEARCH_SCHEMA_ENDPOINT));
  }

  async setEmployeeId(employmentId: string) {
    this.employment = { id: employmentId };
    this.employmentSubject.next(this.employment);

    this.employment = await this.getEmploymentById(employmentId);
    this.employmentSubject.next(this.employment);
  }

  create(
    payload: {
      employee: {
        first_name: string;
        last_name: string;
        email: string;
        phone_number: string;
        address: string;
        country: string;
      };
      employment: {
        employment_position_id: string | null;
        organisation_branch_id: string | null;
        employment_type: string;
        location_type: string;
        location_id: string;
        team_ids: string[];
        new_position: {
          name: string;
          organisation_branch_type_id: string;
          department_id: string | null;
        } | null;
      };
      profile?: {
        resume_object_key?: string | null;
        manager_employment_id?: string | null;
        working_schedule_id?: string | null;
        private_email?: string | null;
        private_phone?: string | null;
        private_address?: string | null;
        private_country_iso2?: string | null;
        emergency_contact_name?: string | null;
        emergency_contact_phone?: string | null;
        date_of_birth?: string | null;
        gender?: string | null;
        marital_status?: string | null;
        passport_number?: string | null;
        work_permit_number?: string | null;
        work_permit_expiry?: string | null;
        timezone?: string | null;
      };
    }
  ): import('rxjs').Observable<any> {
    return this.restApi.post(HR_POST_EMPLOYMENT_ENDPOINT, payload);
  }

  update(employmentId: string, payload: Parameters<EmploymentServices['create']>[0]) {
    return this.restApi.put(formatString(HR_PUT_EMPLOYMENT_ENDPOINT, employmentId), payload);
  }

  async getEmploymentEditContext(employmentId: string) {
    return await firstValueFrom(
      this.restApi.get(formatString(HR_GET_EMPLOYMENT_EDIT_CONTEXT_ENDPOINT, employmentId)),
    );
  }

  respondToEmploymentInvitation(payload: EmploymentInvitationResponsePayload) {
    return this.restApi.post<EmploymentInvitationResponse>(HR_POST_EMPLOYMENT_INVITATION_RESPONSE_ENDPOINT, {
      token: payload.token,
      decision: payload.decision,
    });
  }

  getInvitationContext(token: string) {
    return this.restApi.get<EmploymentInvitationContext>(
      formatString(HR_GET_EMPLOYMENT_INVITATION_CONTEXT_ENDPOINT, encodeURIComponent(token)),
    );
  }

  getMyPendingInvitations(): Promise<MyEmploymentInvitation[]> {
    return firstValueFrom(this.restApi.get<MyEmploymentInvitation[]>(HR_GET_MY_EMPLOYMENT_INVITATIONS_ENDPOINT));
  }

  getMyInvitationContext(id: string) {
    return this.restApi.get<EmploymentInvitationContext>(formatString(HR_GET_MY_EMPLOYMENT_INVITATION_CONTEXT_ENDPOINT, id));
  }

  respondToMyInvitation(id: string, decision: 'accept' | 'deny') {
    return this.restApi.post<EmploymentInvitationResponse>(formatString(HR_POST_MY_EMPLOYMENT_INVITATION_RESPONSE_ENDPOINT, id), { decision });
  }

  async getEmploymentInvitations(params: { status?: string; search?: string; page?: number; perPage?: number } = {}) {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    if (params.search) query.set('search', params.search);
    query.set('page', String(params.page ?? 1));
    query.set('perPage', String(params.perPage ?? 25));
    const suffix = query.toString();
    return await firstValueFrom(this.restApi.get(`${HR_GET_EMPLOYMENT_INVITATIONS_ENDPOINT}?${suffix}`));
  }

  resendEmploymentInvitation(employmentId: string) {
    return this.restApi.post<EmploymentInvitationContext>(formatString(HR_POST_EMPLOYMENT_INVITATION_RESEND_ENDPOINT, employmentId), {});
  }

  async getAll(payload?: Partial<SearchListRequestPayload>) {
    const queryString = buildSearchQueryString(payload);

    return await firstValueFrom(
      this.restApi.get(`${HR_GET_EMPLOYMENTS_ENDPOINT}${queryString}`),
    );
  }

  async getEmploymentById(id: string) {
    return await firstValueFrom(
      this.restApi.get(formatString(HR_GET_EMPLOYMENT_ENDPOINT, id)),
    );
  }

  async getCurrentEmployment() {
    return await firstValueFrom(this.restApi.get(HR_GET_CURRENT_EMPLOYMENT_ENDPOINT));
  }

  async countMyActiveEmployments(): Promise<number> {
    const result = await firstValueFrom(
      this.restApi.get<{ count: number }>(HR_GET_MY_ACTIVE_EMPLOYMENTS_COUNT_ENDPOINT),
    );
    return result.count;
  }

  async getMyEmployments(): Promise<MyEmployment[]> {
    return await firstValueFrom(
      this.restApi.get<MyEmployment[]>(HR_GET_MY_EMPLOYMENTS_ENDPOINT),
    );
  }

  createEmploymentVersion(
    employmentId: string,
    payload: {
      employment_position_id?: string | null;
      departments_id?: string | null;
      organisation_branch_id?: string | null;
      employment_type?: string | null;
      location_type?: string | null;
      started_at?: string | null;
      change_reason?: string | null;
    }
  ) {
    return this.restApi.post(
      formatString(HR_POST_EMPLOYMENT_VERSION_ENDPOINT, employmentId),
      payload,
    );
  }

  promoteEmployment(
    employmentId: string,
    payload: {
      employment_position_id?: string | null;
      departments_id?: string | null;
      organisation_branch_id?: string | null;
      employment_type?: string | null;
      location_type?: string | null;
      started_at?: string | null;
      change_reason?: string | null;
    }
  ) {
    return this.restApi.post(
      formatString(HR_POST_EMPLOYMENT_PROMOTION_ENDPOINT, employmentId),
      payload,
    );
  }

  async getEmploymentHistory(employmentId: string) {
    return await firstValueFrom(
      this.restApi.get(formatString(HR_GET_EMPLOYMENT_HISTORY_ENDPOINT, employmentId)),
    );
  }

  async getEmploymentTimeline(employmentId: string) {
    return await firstValueFrom(
      this.restApi.get(formatString(HR_GET_EMPLOYMENT_TIMELINE_ENDPOINT, employmentId)),
    );
  }

  changeAdminStatus(employmentId: string, isAdmin: boolean) {
    return this.restApi.patch(
      formatString(HR_PATCH_EMPLOYMENT_ADMIN_STATUS_ENDPOINT, employmentId),
      { is_admin: isAdmin },
    );
  }

  mergeEmploymentTimeline(rows: any[]): any[] {
    if (!Array.isArray(rows)) {
      return [];
    }

    const grouped = new Map<string, any[]>();

    for (const row of rows) {
      const key = String(row?.user_id || '');

      if (!key) {
        continue;
      }

      const bucket = grouped.get(key) || [];
      bucket.push(row);
      grouped.set(key, bucket);
    }

    const merged: any[] = [];

    for (const records of grouped.values()) {
      records.sort((left, right) =>
        String(right?.started_at || '').localeCompare(String(left?.started_at || '')),
      );

      const current = records.find((row) => !row?.ended_at) || records[0];

      merged.push({
        ...current,
        employment_versions: records,
        employment_version_count: records.length,
      });
    }

    return merged;
  }

  async checkEmailExists(email: any): Promise<boolean> {
    // TODO: Implement actual API call to check if email exists
    return true;
  }

  async checkPhoneNumberExists(phone: any): Promise<boolean> {
    // TODO: Implement actual API call to check if phone exists
    return false;
  }
}
